import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { UliItemTruckAssign, UliItemTruckInitialState, UliItemTruckListLoad } from './uli-item-truck.actions';
import { UliItemTruckService } from './uli-item-truck.service';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { Error, CustomHttpErrorResponse } from '../../../../../../../common/constants/error';
import { LIST_SERVER_PAGINATION_INITIAL_STATE } from '../../../../../../../common/models/list.model';
import { ListServerPaginationStateModel, NotificationService } from '../../../../../../../common';
import { UliItemTruck } from '../../models';
import { UliItemOverviewLoad } from '../overview';
import { HubBrief, HubContextFacade } from '../../../../../administration';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type UliItemTruckStateModel = ListServerPaginationStateModel<UliItemTruck>;

const initialState: UliItemTruckStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<UliItemTruckStateModel>({
  name: 'uliItemTruck',
  defaults: initialState,
})
@Injectable()
export class UliItemTruckState {
  constructor(
    private uliItemTruckService: UliItemTruckService,
    private notificationService: NotificationService,
    private hubContextFacade: HubContextFacade,
  ) {}

  @Selector()
  static getState(state: UliItemTruckStateModel): UliItemTruckStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: UliItemTruckStateModel): boolean {
    return state.loaded;
  }

  @Action(UliItemTruckListLoad)
  getTruckList(
    ctx: StateContext<UliItemTruckStateModel>,
    { payload: { params, searchCriteria } }: UliItemTruckListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubContextFacade.selectedHub$.pipe(
      filter(Boolean),
      map((hub: HubBrief) => hub.id),
      switchMap((hubId: number) => this.uliItemTruckService.getUliTruckList(params, searchCriteria, hubId)),
      take(1),
      map((list: ListServerPaginationStateModel<UliItemTruck>) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemTruckAssign)
  assignTruck(
    ctx: StateContext<UliItemTruckStateModel>,
    { payload }: UliItemTruckAssign,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemTruckService.assignTruck(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.uli.success.truckAssign');
        ctx.dispatch(new UliItemOverviewLoad(payload.params));
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemTruckInitialState)
  setInitialState(ctx: StateContext<UliItemTruckStateModel>): void {
    ctx.setState(initialState);
  }
}
